import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, App, Alert } from 'antd';
import { UserOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const { Title, Text, Link } = Typography;

// Typewriter component with blinking cursor - Smoother animation
const TypewriterText: React.FC<{ text: string; delay?: number }> = ({ text, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let i = 0;

    const typeNextChar = () => {
      if (i < text.length) {
        setDisplayedText(text.slice(0, i + 1));
        i++;
        timeout = setTimeout(typeNextChar, 50); // Faster, smoother typing (was 80)
      }
    };

    timeout = setTimeout(typeNextChar, delay);

    // Blinking cursor
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => {
      clearTimeout(timeout);
      clearInterval(cursorInterval);
    };
  }, [text, delay]);

  return (
    <span>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {displayedText}
      </motion.span>
      <motion.span
        animate={{ opacity: showCursor ? 1 : 0 }}
        transition={{ duration: 0.1 }}
      >
        _
      </motion.span>
    </span>
  );
};

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { notification } = App.useApp();

  const slides = [
  {
    title: "Enterprise Intelligence Reimagined",
    subtitle: "RAG + Agentic AI for real-time decision-making"
  },
  {
    title: "Unified Control Tower",
    subtitle: "Monitor, analyze, and act — all from one intelligent hub"
  },
  {
    title: "Dynamic Reports & Dashboards",
    subtitle: "Auto-generate insights with natural language prompts"
  },
  {
    title: "Smart Workflow Automation",
    subtitle: "Create dynamic schedulers and triggers effortlessly"
  },
  {
    title: "Context-Aware Communication",
    subtitle: "Collaborate across teams with AI-driven context syncing"
  },
  {
    title: "Adaptive Data Orchestration",
    subtitle: "Connect enterprise data silos into one intelligent fabric"
  },
  {
    title: "Actionable Insights in Real Time",
    subtitle: "From data retrieval to automated execution — instantly"
  },
  {
    title: "Your Enterprise Control Tower",
    subtitle: "See everything. Understand everything. Do everything."
  }
];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    console.log('🔐 Login attempt:', values.email);

    try {
      // Bypass for testing credentials
      if (values.email === 'admin@admin.com' && values.password === 'admin') {
        console.log('🔓 Using test bypass credentials');

        // Create mock user data
        const mockUserData = {
          id: 'test-user-1',
          name: 'Admin User',
          email: 'admin@admin.com',
          role: 'admin' as 'admin' | 'project-manager' | 'supervisor' | 'worker' | 'procurement',
          department: 'Administration',
          isActive: true,
          userid: 'admin',
          firstName: 'Admin',
          phoneNumber: '+1234567890',
          orgid: 1,
          orgType: 'Enterprise',
          genericRole: 'Administrator',
          userDetailsId: 1,
          roleName: 'System Administrator',
          orgName: 'Test Organization',
          siteid: [1],
          modules: {
            all: ['read', 'write', 'delete']
          },
        };

        // For test credentials, generate a mock session_id
        const mockSessionId = 'test_session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
        localStorage.setItem('session_id', mockSessionId);
        localStorage.setItem('user', JSON.stringify(mockUserData));

        // Update auth context
        login(mockUserData);

        // Show success notification
        notification.success({
          message: 'Success (Test Mode)',
          description: 'Welcome back, Admin! Using test credentials.',
          placement: 'topRight',
          duration: 3,
        });

        // Navigate to dashboard
        navigate('/');
        setLoading(false);
        return;
      }

      // Import auth service
      const authService = await import('../services/api/authService');

      // Step 1: Login and get session_id
      console.log('📞 Calling login API...');
      const loginResponse = await authService.login(
        values.email,
        values.password,
        'Buyer',
        true
      );
      console.log('✅ Login response:', loginResponse);

      // Step 2: Check if login was successful
      if (loginResponse.status !== true) {
        throw new Error(loginResponse.message || 'Login failed');
      }

      // Step 3: Fetch user information (session_id sent automatically)
      console.log('📞 Fetching user info...');
      const userInfo = await authService.getUserInfo();
      console.log('✅ User info:', userInfo);

      // Step 4: Map UserInfo to User type for context
      const userData = {
        id: userInfo.id.toString(),
        name: userInfo.firstName,
        email: userInfo.email,
        role: 'admin' as 'admin' | 'project-manager' | 'supervisor' | 'worker' | 'procurement',
        department: userInfo.orgName || 'General',
        isActive: true,
        // Store additional fields
        userid: userInfo.userid,
        firstName: userInfo.firstName,
        phoneNumber: userInfo.phoneNumber,
        orgid: userInfo.orgid,
        orgType: userInfo.orgType,
        genericRole: userInfo.genericRole,
        userDetailsId: userInfo.userDetailsId,
        roleName: userInfo.roleName,
        orgName: userInfo.orgName,
        siteid: userInfo.siteid,
        modules: userInfo.modules,
      };

      // Step 5: Store user data in localStorage (session_id already stored by login)
      localStorage.setItem('user', JSON.stringify(userData));

      // Step 6: Update auth context
      login(userData);

      // Step 7: Show success notification
      notification.success({
        message: 'Success',
        description: `Welcome back, ${userInfo.firstName}!`,
        placement: 'topRight',
        duration: 3,
      });

      // Step 8: Navigate to dashboard
      navigate('/');

    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.log('📋 Error response data:', error.response?.data);

      let errorMessage = '';

      // Handle CORS/Network errors
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        errorMessage = 'Unable to connect to server. Please check if the backend server is running and CORS is properly configured.';

        notification.error({
          message: 'Connection Error',
          description: errorMessage,
          placement: 'topRight',
          duration: 6,
        });
      }
      // Handle API response errors
      else if (error.response?.data) {
        let errorData = error.response.data;

        // If errorData is a string, try to parse it
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            errorMessage = errorData;
          }
        }

        // Extract error message
        if (!errorMessage) {
          // Check if response has status: false (API error format)
          if (errorData.status === false) {
            errorMessage = errorData.message || 'Login failed';
          }
          // Fallback to other message fields
          else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else {
            errorMessage = 'Please check your credentials and try again.';
          }
        }

        notification.error({
          message: 'Login Failed',
          description: errorMessage,
          placement: 'topRight',
          duration: 5,
        });
      }
      // Generic error
      else {
        errorMessage = error.message || 'An unexpected error occurred. Please try again.';

        notification.error({
          message: 'Error',
          description: errorMessage,
          placement: 'topRight',
          duration: 5,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#262624',
        display: 'flex',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Multi-color gradient ripples - Extended to whole page */}
      {/* Purple ripple */}
      <motion.div
        style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(147, 51, 234, 0.25) 0%, rgba(168, 85, 247, 0.18) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, 100, 0],
          y: [0, 60, 0],
          scale: [1, 1.3, 1]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Blue ripple */}
      <motion.div
        style={{
          position: 'absolute',
          top: '25%',
          right: '15%',
          width: '550px',
          height: '550px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.28) 0%, rgba(96, 165, 250, 0.2) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(90px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, -80, 0],
          y: [0, 50, 0],
          scale: [1, 1.35, 1]
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />

      {/* Cyan ripple */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: '15%',
          left: '25%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.22) 0%, rgba(34, 211, 238, 0.15) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(85px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, -70, 0],
          y: [0, -50, 0],
          scale: [1, 1.3, 1]
        }}
        transition={{
          duration: 11,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />

      {/* Pink ripple */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '520px',
          height: '520px',
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, rgba(244, 114, 182, 0.14) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(90px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, -90, 0],
          y: [0, -45, 0],
          scale: [1, 1.25, 1]
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
      />

      {/* Orange/Amber ripple */}
      <motion.div
        style={{
          position: 'absolute',
          top: '45%',
          left: '10%',
          width: '480px',
          height: '480px',
          background: 'radial-gradient(circle, rgba(251, 146, 60, 0.18) 0%, rgba(253, 186, 116, 0.12) 35%, transparent 65%)',
          borderRadius: '50%',
          filter: 'blur(75px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
        animate={{
          x: [0, 80, 0],
          y: [0, -55, 0],
          scale: [1, 1.28, 1]
        }}
        transition={{
          duration: 13,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5
        }}
      />

      {/* Left Side - Animated Text Slideshow */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '80px',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1
        }}
        className="login-left-panel"
      >
        {/* Content Container for proper alignment */}
        <div style={{ maxWidth: '650px', width: '100%' }}>
        {/* Logo/Brand - Futuristic AI Design */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: 60, position: 'relative' }}
        >
          {/* Animated glow rings behind text */}
          <motion.div
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              width: '120px',
              height: '120px',
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }}
          >
            <motion.div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(59, 130, 246, 0.3)',
              }}
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(147, 51, 234, 0.3)',
              }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.4, 0, 0.4],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
            <motion.div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(6, 182, 212, 0.3)',
              }}
              animate={{
                scale: [1, 1.6, 1],
                opacity: [0.3, 0, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
            />
          </motion.div>

          {/* Main ControlX text with gradient animation */}
          <motion.div
            style={{
              position: 'relative',
              display: 'inline-block'
            }}
          >
            <motion.div
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4, #3b82f6)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: 48,
                fontWeight: 900,
                marginBottom: 8,
                letterSpacing: '1px',
                fontFamily: '"Orbitron", sans-serif',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Control<img
                src="https://procurexuatstg.blob.core.windows.net/procurexuatblob/prc7.png"
                alt="X"
                style={{
                  height: '48px',
                  width: 'auto',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginLeft: '2px',
                  filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))'
                }}
              />
            </motion.div>

            {/* Animated underline */}
            <motion.div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, transparent, #3b82f6, #8b5cf6, transparent)',
                borderRadius: '2px'
              }}
              animate={{
                opacity: [0.3, 0.8, 0.3],
                scaleX: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Particle effects */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                style={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  background: i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#06b6d4',
                  borderRadius: '50%',
                  boxShadow: `0 0 10px ${i % 3 === 0 ? '#3b82f6' : i % 3 === 1 ? '#8b5cf6' : '#06b6d4'}`,
                }}
                animate={{
                  x: [0, (Math.random() - 0.5) * 200],
                  y: [0, (Math.random() - 0.5) * 100],
                  opacity: [0, 0.8, 0],
                  scale: [0, 1.5, 0]
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: i * 0.5,
                  ease: "easeOut"
                }}
              />
            ))}
          </motion.div>

          {/* Subtitle with typing effect appearance */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Text style={{
              color: '#9ca3af',
              fontSize: 15,
              fontWeight: 500,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              fontFamily: '"Rajdhani", sans-serif'
            }}>
              Your Enterprise. Powered by Intelligence.
            </Text>
          </motion.div>
        </motion.div>

        {/* Animated Slides */}
        <div style={{ minHeight: '200px', position: 'relative', maxWidth: '700px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Title
                level={2}
                style={{
                  color: '#FFFFFF',
                  fontSize: 48,
                  fontWeight: 700,
                  marginBottom: 16,
                  lineHeight: 1.2,
                  letterSpacing: '-1px'
                }}
              >
                <TypewriterText text={slides[currentSlide].title} />
              </Title>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <Text
                  style={{
                    color: '#9ca3af',
                    fontSize: 20,
                    lineHeight: 1.6
                  }}
                >
                  {slides[currentSlide].subtitle}
                </Text>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide Indicators - Brighter blue */}
        <div style={{ display: 'flex', gap: 12, marginTop: 60 }}>
          {slides.map((_, index) => (
            <motion.div
              key={index}
              style={{
                width: currentSlide === index ? 40 : 12,
                height: 4,
                background: currentSlide === index ? '#3b82f6' : '#3A3A38',
                borderRadius: 2,
                cursor: 'pointer',
                boxShadow: currentSlide === index ? '0 0 10px rgba(59, 130, 246, 0.5)' : 'none'
              }}
              animate={{
                width: currentSlide === index ? 40 : 12,
              }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              onClick={() => setCurrentSlide(index)}
              whileHover={{ scale: 1.2 }}
            />
          ))}
        </div>

        </div>
        {/* End Content Container */}

        {/* Footer Text */}
        <motion.div
          style={{ position: 'absolute', bottom: 40, left: 80 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <Text style={{ color: '#6B6B69', fontSize: 11 }}>
            © 2025 ControlX. Powered by ProcureX
          </Text>
        </motion.div>
      </div>

      {/* Right Side - Login Form */}
      <div
        style={{
          width: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(20px)',
          borderLeft: '1px solid rgba(58, 58, 56, 0.3)',
          position: 'relative',
          zIndex: 1
        }}
        className="login-right-panel"
      >
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ width: '100%', maxWidth: '420px' }}
        >
          <Card
            style={{
              background: 'transparent',
              border: 'none',
              boxShadow: 'none'
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Title */}
            <div style={{ marginBottom: 40 }}>
              <Title
                level={3}
                style={{
                  color: '#FFFFFF',
                  marginBottom: 8,
                  fontWeight: 600
                }}
              >
                Welcome back
              </Title>
              <Text style={{ color: '#8A8A8A', fontSize: 14 }}>
                Sign in to your account to continue
              </Text>
            </div>

            {/* Access Notice */}
            <Alert
              message={
                <span style={{ color: '#3b82f6', fontWeight: 600, fontSize: 12 }}>
                  Enterprise Access Required
                </span>
              }
              description={
                <span style={{ color: '#B8B8B8', fontSize: 11 }}>
                  Contact your system administrator for access requests.
                </span>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{
                marginBottom: 32,
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: 8
              }}
            />

            {/* Login Form */}
            <Form
              name="login"
              onFinish={onFinish}
              layout="vertical"
              requiredMark={false}
              size="large"
            >
              <Form.Item
                name="email"
                label={<span style={{ color: '#B8B8B8', fontSize: 13 }}>Email</span>}
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#6B6B69' }} />}
                  placeholder="your.email@company.com"
                  style={{
                    background: '#262624',
                    border: '1px solid #3A3A38',
                    color: '#FFFFFF',
                    height: 48
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span style={{ color: '#B8B8B8', fontSize: 13 }}>Password</span>}
                rules={[{ required: true, message: 'Please enter your password' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#6B6B69' }} />}
                  placeholder="Enter your password"
                  style={{
                    background: '#262624',
                    border: '1px solid #3A3A38',
                    color: '#FFFFFF',
                    height: 48
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 32, marginTop: 32 }}>
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    style={{
                      height: 52,
                      fontSize: 15,
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      border: 'none',
                      borderRadius: 8,
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                    }}
                  >
                    Sign In
                  </Button>
                </motion.div>
              </Form.Item>
            </Form>

            {/* Help Text */}
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Text style={{ color: '#6B6B69', fontSize: 12 }}>
                Need help?{' '}
                <Link style={{ color: '#3b82f6', fontWeight: 500, fontSize: 12 }}>
                  Contact Administrator
                </Link>
              </Text>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 1024px) {
          .login-left-panel {
            display: none !important;
          }
          .login-right-panel {
            width: 100% !important;
            border-left: none !important;
            padding: 40px 24px !important;
          }
        }

        @media (max-width: 480px) {
          .login-right-panel {
            padding: 24px 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
